import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Container, Form } from "react-bootstrap";
import Header from "../_Common/header";
import WhatsAppFab from "../_Common/WhatsAppFab";
import FleetService from "../../services/fleetService";
import useSeo from "../../hooks/useSeo";
import "./fleet-detail.scss";

const categoryInfo = {
    sail_boats_tab: { type: "Sail Boat", icon: "ri-sailboat-line" },
    sail_yachts_tab: { type: "Sail Yacht", icon: "ri-ship-line" },
    motor_yachts_tab: { type: "Motor Yacht", icon: "ri-ship-2-line" },
    speed_boats_tab: { type: "Speed Boat", icon: "ri-speed-mini-line" },
    ferry_boats_tab: { type: "Ferry Boat", icon: "ri-ferry-line" }
};

export default function FleetDetail() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [boat, setBoat] = useState(null);

    // Booking Form State
    const [bookingDate, setBookingDate] = useState("");
    const [selectedTrip, setSelectedTrip] = useState("");
    const [guestCount, setGuestCount] = useState(1);

    // Lightbox State
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [lightboxIndex, setLightboxIndex] = useState(0);

    // Swipe State
    const [touchStart, setTouchStart] = useState(null);
    const [touchEnd, setTouchEnd] = useState(null);




    const images = boat ? (boat.gallery && boat.gallery.length > 0 ? boat.gallery : [boat.image]) : [];

    useEffect(() => {
        window.scrollTo(0, 0);
        if (slug) {
            const data = FleetService.getFleetBySlug(slug);
            if (data) {
                setBoat(data);
                if (Array.isArray(data.trip) && data.trip.length > 0) {
                    setSelectedTrip(data.trip[0]);
                } else if (typeof data.trip === 'string') {
                    setSelectedTrip(data.trip);
                }
            } else {
                console.error("Boat not found");
                navigate("/full-fleet");
            }
        }
    }, [slug, navigate]);

    useEffect(() => {
        if (!lightboxOpen) return undefined;
        const onKey = (e) => {
            if (e.key === "Escape") setLightboxOpen(false);
            if (e.key === "ArrowRight") setLightboxIndex((prev) => (prev + 1) % images.length);
            if (e.key === "ArrowLeft") setLightboxIndex((prev) => (prev - 1 + images.length) % images.length);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [lightboxOpen, images.length]);

    const seoName = boat ? boat.name : "Fleet";
    const seoType = boat ? (categoryInfo[boat.category]?.type || boat.type || "Yacht") : "Yacht";
    const seoUrl = `https://yachtmumbai.in/fleet-detail/${slug}`;
    const seoGuests = boat ? parseInt(String(boat.guestCount).replace(/\D/g, ''), 10) : 0;

    useSeo({
        title: boat
            ? `${seoName} ${seoType} Rental in Mumbai | Ocean Tiger`
            : "Fleet Details | Ocean Tiger",
        description: boat
            ? `${seoName} ${seoType.toLowerCase()} on charter from Gateway of India, Mumbai${seoGuests ? `, seats up to ${seoGuests} guests` : ""}. ${boat.about}`.slice(0, 160)
            : "Vessel details for yacht and boat charters from Gateway of India, Mumbai.",
        canonical: seoUrl,
        noindex: !boat,
        image: boat && boat.image ? `https://yachtmumbai.in${boat.image}` : undefined,
        jsonLd: boat
            ? {
                "@context": "https://schema.org",
                "@graph": [
                    {
                        "@type": "BreadcrumbList",
                        itemListElement: [
                            { "@type": "ListItem", position: 1, name: "Home", item: "https://yachtmumbai.in/" },
                            { "@type": "ListItem", position: 2, name: "Our Fleet", item: "https://yachtmumbai.in/full-fleet" },
                            { "@type": "ListItem", position: 3, name: seoName, item: seoUrl },
                        ],
                    },
                    {
                        "@type": "Service",
                        name: `${seoName} ${seoType} Rental, Mumbai`,
                        serviceType: `${seoType} charter`,
                        description: boat.about,
                        url: seoUrl,
                        areaServed: { "@type": "City", name: "Mumbai" },
                        provider: {
                            "@type": "LocalBusiness",
                            name: "Ocean Tiger",
                            telephone: "+91 9920088412",
                            address: {
                                "@type": "PostalAddress",
                                streetAddress: "Jetty No. 5, Opposite Taj Hotel, Apollo Bunder, Colaba",
                                addressLocality: "Mumbai",
                                addressRegion: "Maharashtra",
                                postalCode: "400001",
                                addressCountry: "IN",
                            },
                        },
                    },
                ],
            }
            : null,
    });

    if (!boat) return <div className="p-5 text-center">Loading...</div>;

    const info = categoryInfo[boat.category] || { type: "Yacht", icon: "ri-anchor-line" };
    const trips = Array.isArray(boat.trip) ? boat.trip : [boat.trip];
    const capacityNum = parseInt(boat.guestCount.replace(/\D/g, '')) || 10;
    const typeLower = info.type.toLowerCase();

    // Gallery: one lead image plus up to four tiles; the last tile shows how many more there are
    const galleryTiles = images.slice(1, 5);
    const hiddenCount = images.length - 1 - galleryTiles.length;

    const handleWhatsAppEnquiry = (e) => {
        e.preventDefault();
        const formattedDate = bookingDate ? new Date(bookingDate).toLocaleDateString('en-GB', {
            day: 'numeric', month: 'long', year: 'numeric'
        }) : "Date Not Selected";

        const message = `Hi Yacht Mumbai! I'd like to book:

🚢 Fleet: ${info.type} - ${boat.name}
📍 Trip: ${selectedTrip}
👥 Guests: ${guestCount}
📅 Date: ${formattedDate}

Please share availability and pricing details.`;

        const encodedMessage = encodeURIComponent(message);
        window.open(`https://wa.me/919920088412?text=${encodedMessage}`, '_blank');
    };

    const handleGuestChange = (delta) => {
        const newVal = guestCount + delta;
        if (newVal >= 1 && newVal <= capacityNum) {
            setGuestCount(newVal);
        }
    };

    const openLightbox = (index) => {
        setLightboxIndex(index);
        setLightboxOpen(true);
    };

    const closeLightbox = () => {
        setLightboxOpen(false);
    };

    const nextImage = (e) => {
        e.stopPropagation();
        setLightboxIndex((prev) => (prev + 1) % images.length);
    };

    const prevImage = (e) => {
        e.stopPropagation();
        setLightboxIndex((prev) => (prev - 1 + images.length) % images.length);
    };

    const handleBack = () => {
        const params = new URLSearchParams(location.search);
        const category = params.get('category') || boat.category;
        navigate(`/full-fleet?category=${category}`);
    };

    // Swipe Handlers
    const minSwipeDistance = 50;

    const onTouchStart = (e) => {
        setTouchEnd(null);
        setTouchStart(e.targetTouches[0].clientX);
    };

    const onTouchMove = (e) => {
        setTouchEnd(e.targetTouches[0].clientX);
    };

    const onTouchEnd = () => {
        if (!touchStart || !touchEnd) return;

        const distance = touchStart - touchEnd;
        const isLeftSwipe = distance > minSwipeDistance;
        const isRightSwipe = distance < -minSwipeDistance;

        if (isLeftSwipe) {
            setLightboxIndex((prev) => (prev + 1) % images.length);
        }
        if (isRightSwipe) {
            setLightboxIndex((prev) => (prev - 1 + images.length) % images.length);
        }
    };

    return (
        <React.Fragment>
            <Header />

            <div className="fleet-detail-page">
                <Container>
                    {/* HEADER */}
                    <div className="detail-top-nav">
                        <button type="button" className="back-btn" onClick={handleBack}>
                            <i className="ri-arrow-left-line"></i>
                            <span>All {info.type}s</span>
                        </button>
                    </div>

                    <header className="detail-head" data-aos="fade-up">
                        <div className="detail-head-title">
                            <p className="eyebrow"><span>{info.type}</span></p>
                            <h1>{boat.name}</h1>
                        </div>
                        <dl className="detail-facts">
                            <div>
                                <dt>Capacity</dt>
                                <dd>{capacityNum} <span>guests</span></dd>
                            </div>
                            <div>
                                <dt>Trips</dt>
                                <dd>{String(trips.length).padStart(2, "0")}</dd>
                            </div>
                            <div>
                                <dt>Departs</dt>
                                <dd>Gateway of India</dd>
                            </div>
                        </dl>
                    </header>

                    {/* GALLERY */}
                    <div className={`bento-grid-gallery count-${galleryTiles.length}`}>
                        <button type="button" className="gallery-item item-main" onClick={() => openLightbox(0)}>
                            <img src={images[0]} alt={`${boat.name} ${typeLower} at Gateway of India, Mumbai`} />
                        </button>
                        {galleryTiles.map((img, idx) => (
                            <button type="button" className="gallery-item item-sub" key={img} onClick={() => openLightbox(idx + 1)}>
                                <img src={img} alt={`${boat.name} ${typeLower} view ${idx + 2}`} loading="lazy" />
                                {hiddenCount > 0 && idx === galleryTiles.length - 1 && (
                                    <span className="gallery-more">+{hiddenCount}</span>
                                )}
                            </button>
                        ))}
                    </div>

                    {/* MOBILE GALLERY · swipe strip */}
                    <div className="mobile-gallery-strip">
                        {images.map((img, i) => (
                            <button type="button" key={img} onClick={() => openLightbox(i)}>
                                <img src={img} alt={`${boat.name} ${typeLower} view ${i + 1}`} loading={i ? "lazy" : undefined} />
                                <span className="strip-count">{i + 1} / {images.length}</span>
                            </button>
                        ))}
                    </div>

                    <div className="detail-layout">
                        {/* SPEC TABLE */}
                        <div className="detail-main">
                            <section className="detail-row">
                                <h2 className="detail-row-label">About</h2>
                                <p className="detail-about">{boat.about}</p>
                            </section>

                            {boat.points && boat.points.length > 0 && (
                                <section className="detail-row">
                                    <h2 className="detail-row-label">Highlights</h2>
                                    <ul className="detail-list">
                                        {boat.points.map((point, index) => (
                                            <li key={index}>
                                                <i className="ri-check-line"></i>
                                                <span>{point}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </section>
                            )}

                            <section className="detail-row">
                                <h2 className="detail-row-label">Trips</h2>
                                <ul className="detail-list">
                                    {trips.map((t, i) => (
                                        <li key={i}>
                                            <span className="detail-list-index">{String(i + 1).padStart(2, "0")}</span>
                                            <span>{t}</span>
                                        </li>
                                    ))}
                                </ul>
                            </section>

                            <section className="detail-row">
                                <h2 className="detail-row-label">Capacity</h2>
                                <p className="detail-about">Up to {capacityNum} guests</p>
                            </section>
                        </div>

                        {/* BOOKING CARD */}
                        <aside className="booking-form-section">
                            <h2 className="booking-title">Book Your Experience</h2>
                            <p className="subtitle">Secure your voyage on the {boat.name} today</p>

                            <Form onSubmit={handleWhatsAppEnquiry}>
                                <div className="form-group-custom">
                                    <label htmlFor="booking-date">Date</label>
                                    <Form.Control
                                        id="booking-date"
                                        type="date"
                                        required
                                        value={bookingDate}
                                        onChange={(e) => setBookingDate(e.target.value)}
                                        min={new Date().toISOString().split('T')[0]}
                                    />
                                </div>

                                <div className="form-group-custom">
                                    <label htmlFor="booking-trip">Trip</label>
                                    <Form.Select
                                        id="booking-trip"
                                        value={selectedTrip}
                                        onChange={(e) => setSelectedTrip(e.target.value)}
                                    >
                                        {trips.map((t) => (
                                            <option key={t} value={t}>{t}</option>
                                        ))}
                                    </Form.Select>
                                </div>

                                <div className="form-group-custom">
                                    <label id="booking-guests">Guests <span>max {capacityNum}</span></label>
                                    <div className="guest-input-wrapper" role="group" aria-labelledby="booking-guests">
                                        <button type="button" aria-label="Fewer guests" onClick={() => handleGuestChange(-1)} disabled={guestCount <= 1}>
                                            <i className="ri-subtract-line"></i>
                                        </button>
                                        <span className="guest-val" aria-live="polite">{guestCount}</span>
                                        <button type="button" aria-label="More guests" onClick={() => handleGuestChange(1)} disabled={guestCount >= capacityNum}>
                                            <i className="ri-add-line"></i>
                                        </button>
                                    </div>
                                </div>

                                <button type="submit" className="btn-ink btn-block enquire-btn">
                                    <i className="ri-whatsapp-line"></i>
                                    <span>Enquire on WhatsApp</span>
                                </button>
                            </Form>

                            <p className="booking-note">
                                <i className="ri-map-pin-line"></i>
                                <span>Jetty No. 5, Opposite Taj Hotel, Apollo Bunder, Colaba, Mumbai</span>
                            </p>
                        </aside>
                    </div>
                </Container>

                {/* LIGHTBOX MODAL */}
                {lightboxOpen && (
                    <div className="lightbox-overlay" onClick={closeLightbox} role="dialog" aria-modal="true" aria-label={`${boat.name} photos`}>
                        <button type="button" className="close-btn" onClick={closeLightbox} aria-label="Close"><i className="ri-close-line"></i></button>

                        <div
                            className="lightbox-content"
                            onClick={e => e.stopPropagation()}
                            onTouchStart={onTouchStart}
                            onTouchMove={onTouchMove}
                            onTouchEnd={onTouchEnd}
                        >
                            <button type="button" className="nav-btn prev" onClick={prevImage} aria-label="Previous photo"><i className="ri-arrow-left-s-line"></i></button>
                            <img src={images[lightboxIndex]} alt={`${boat.name} ${typeLower} enlarged view ${lightboxIndex + 1}`} />
                            <button type="button" className="nav-btn next" onClick={nextImage} aria-label="Next photo"><i className="ri-arrow-right-s-line"></i></button>
                        </div>

                        <div className="image-counter">
                            {String(lightboxIndex + 1).padStart(2, "0")} / {String(images.length).padStart(2, "0")}
                        </div>
                    </div>
                )}

                <WhatsAppFab />
            </div>
        </React.Fragment>
    );
}
